import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './components/AuthContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Launchpad from './pages/Launchpad';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Accounts from './pages/Accounts';
import NewDeployment from './pages/NewDeployment';
import SavedSites from './pages/SavedSites';
import History from './pages/History';
import Settings from './pages/Settings';
import BucketManager from './pages/BucketManager';
import HerokuDashboard from './pages/HerokuDashboard';

import HerokuAccounts from './pages/HerokuAccounts';
import HerokuTemplates from './pages/HerokuTemplates';
import HerokuNewDeployment from './pages/HerokuNewDeployment';
import HerokuManageUrls from './pages/HerokuManageUrls';
import HerokuExistingManager from './pages/HerokuExistingManager';
import DOManageUrls from './pages/DOManageUrls';
import DOBulkOverride from './pages/DOBulkOverride';
import RDPDashboard from './pages/RDPDashboard';
import RDPAccounts from './pages/RDPAccounts';
import RDPNewDeployment from './pages/RDPNewDeployment';
import RDPMachines from './pages/RDPMachines';
import VultrAccounts from './pages/VultrAccounts';
import VultrNew from './pages/VultrNew';
import VultrMachines from './pages/VultrMachines';
import WPBAccounts from './pages/WPBAccounts';
import WPBNew from './pages/WPBNew';
import WPBSites from './pages/WPBSites';
import DCBotRunner from './pages/DCBotRunner';
import DOProxyDeployer from './pages/DOProxyDeployer';
import AzureDashboard from './pages/AzureDashboard';
import AzureAccounts from './pages/AzureAccounts';
import AzureNewDeployment from './pages/AzureNewDeployment';
import AzureManageUrls from './pages/AzureManageUrls';
import AzureMasterWipe from './pages/AzureMasterWipe';
import AzureTester from './pages/AzureTester';
import AzureFileUploader from './pages/AzureFileUploader';

import DOMasterWipe from './pages/DOMasterWipe';
import HerokuMasterWipe from './pages/HerokuMasterWipe';

import AzurePhpDeployment from './pages/AzurePhpDeployment';

import CocktailDashboard from './pages/CocktailDashboard';
import CocktailNewDeployment from './pages/CocktailNewDeployment';
import CocktailManageUrls from './pages/CocktailManageUrls';
import NamecheapProDashboard from './pages/NamecheapProDashboard';
import NamecheapProNewDeployment from './pages/NamecheapProNewDeployment';
import NamecheapAzureThreesome from './pages/NamecheapAzureThreesome';
import HostingerAccounts from './pages/HostingerAccounts';
import DomainAccounts from './pages/DomainAccounts';

import ForgeDashboard from './pages/ForgeDashboard';
import ForgeAccounts from './pages/ForgeAccounts';
import ForgeTemplates from './pages/ForgeTemplates';
import ForgeNewDeployment from './pages/ForgeNewDeployment';
import ForgeManageUrls from './pages/ForgeManageUrls';
import ForgeMasterWipe from './pages/ForgeMasterWipe';
import AmplifyDeployer from './pages/AmplifyDeployer';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isLaunchpad = location.pathname === '/';

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (isLaunchpad) {
    return (
      <div className="min-h-screen bg-background text-white relative">
        <div className="absolute top-6 right-6 z-10">
           <button onClick={() => {
              const event = new CustomEvent('trigger-logout');
              window.dispatchEvent(event);
           }} className="text-sm font-bold text-red-500 uppercase tracking-widest hover:text-red-400">LOGOUT</button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 p-6 md:p-12 overflow-y-auto">
        <div className="max-w-[1600px] mx-auto w-full">
          {children}
        </div>
      </main>
    </div>
  );
};

const LogoutListener = () => {
  const { logout } = useAuth();
  React.useEffect(() => {
    const handleLogout = () => logout();
    window.addEventListener('trigger-logout', handleLogout);
    return () => window.removeEventListener('trigger-logout', handleLogout);
  }, [logout]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <LogoutListener />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route path="/" element={<ProtectedRoute><Launchpad /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          
          <Route path="/do" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/do/saved-sites" element={<ProtectedRoute><SavedSites /></ProtectedRoute>} />
          <Route path="/do/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
          <Route path="/do/deployments/new" element={<ProtectedRoute><NewDeployment /></ProtectedRoute>} />
          <Route path="/do/proxy" element={<ProtectedRoute><DOProxyDeployer /></ProtectedRoute>} />
          <Route path="/do/deployments/:id/manager" element={<ProtectedRoute><BucketManager /></ProtectedRoute>} />
          <Route path="/do/history" element={<Navigate to="/do/urls" replace />} />
          <Route path="/do/urls" element={<ProtectedRoute><DOManageUrls /></ProtectedRoute>} />
          <Route path="/do/bulk-override" element={<ProtectedRoute><DOBulkOverride /></ProtectedRoute>} />
          <Route path="/do/wipe" element={<ProtectedRoute><DOMasterWipe /></ProtectedRoute>} />
          <Route path="/do/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          
          <Route path="/heroku" element={<ProtectedRoute><HerokuDashboard /></ProtectedRoute>} />
          <Route path="/heroku/accounts" element={<ProtectedRoute><HerokuAccounts /></ProtectedRoute>} />
          <Route path="/heroku/templates" element={<ProtectedRoute><HerokuTemplates /></ProtectedRoute>} />
          <Route path="/heroku/deployments/new" element={<ProtectedRoute><HerokuNewDeployment /></ProtectedRoute>} />
          <Route path="/heroku/history" element={<Navigate to="/heroku/urls" replace />} />
          <Route path="/heroku/urls" element={<ProtectedRoute><HerokuManageUrls /></ProtectedRoute>} />
          <Route path="/heroku/existing-manager" element={<ProtectedRoute><HerokuExistingManager /></ProtectedRoute>} />
          <Route path="/heroku/wipe" element={<ProtectedRoute><HerokuMasterWipe /></ProtectedRoute>} />

          <Route path="/amplify" element={<ProtectedRoute><AmplifyDeployer /></ProtectedRoute>} />
          
          <Route path="/azure" element={<ProtectedRoute><AzureDashboard /></ProtectedRoute>} />
          <Route path="/azure/saved-sites" element={<ProtectedRoute><SavedSites /></ProtectedRoute>} />
          <Route path="/azure/accounts" element={<ProtectedRoute><AzureAccounts /></ProtectedRoute>} />
          <Route path="/azure/deployments/new" element={<ProtectedRoute><AzureNewDeployment /></ProtectedRoute>} />
          <Route path="/azure/deployments/php/new" element={<ProtectedRoute><AzurePhpDeployment /></ProtectedRoute>} />
          <Route path="/azure/urls" element={<ProtectedRoute><AzureManageUrls /></ProtectedRoute>} />
          <Route path="/azure/uploader" element={<ProtectedRoute><AzureFileUploader /></ProtectedRoute>} />
          <Route path="/azure/wipe" element={<ProtectedRoute><AzureMasterWipe /></ProtectedRoute>} />
          <Route path="/azure/tester" element={<ProtectedRoute><AzureTester /></ProtectedRoute>} />
          
          <Route path="/cocktail" element={<ProtectedRoute><CocktailDashboard /></ProtectedRoute>} />
          <Route path="/cocktail/deployments/new" element={<ProtectedRoute><CocktailNewDeployment /></ProtectedRoute>} />
          <Route path="/cocktail/urls" element={<ProtectedRoute><CocktailManageUrls /></ProtectedRoute>} />
          
          <Route path="/namecheap" element={<ProtectedRoute><NamecheapProDashboard /></ProtectedRoute>} />
          <Route path="/namecheap/accounts" element={<ProtectedRoute><DomainAccounts /></ProtectedRoute>} />
          <Route path="/namecheap/deployments/new" element={<ProtectedRoute><NamecheapProNewDeployment /></ProtectedRoute>} />
          <Route path="/namecheap/azure/new" element={<ProtectedRoute><NamecheapAzureThreesome /></ProtectedRoute>} />
          <Route path="/namecheap/hostinger-accounts" element={<ProtectedRoute><HostingerAccounts /></ProtectedRoute>} />

          <Route path="/forge" element={<ProtectedRoute><ForgeDashboard /></ProtectedRoute>} />
          <Route path="/forge/accounts" element={<ProtectedRoute><ForgeAccounts /></ProtectedRoute>} />

          <Route path="/rdp" element={<ProtectedRoute><RDPDashboard /></ProtectedRoute>} />
          <Route path="/rdp/new" element={<ProtectedRoute><RDPNewDeployment /></ProtectedRoute>} />
          <Route path="/rdp/machines" element={<ProtectedRoute><RDPMachines /></ProtectedRoute>} />
          <Route path="/rdp/accounts" element={<ProtectedRoute><RDPAccounts /></ProtectedRoute>} />

          <Route path="/vultr" element={<ProtectedRoute><VultrMachines /></ProtectedRoute>} />
          <Route path="/vultr/new" element={<ProtectedRoute><VultrNew /></ProtectedRoute>} />
          <Route path="/vultr/machines" element={<ProtectedRoute><VultrMachines /></ProtectedRoute>} />
          <Route path="/vultr/accounts" element={<ProtectedRoute><VultrAccounts /></ProtectedRoute>} />
          <Route path="/wordpress" element={<ProtectedRoute><WPBSites /></ProtectedRoute>} />
          <Route path="/wordpress/new" element={<ProtectedRoute><WPBNew /></ProtectedRoute>} />
          <Route path="/wordpress/sites" element={<ProtectedRoute><WPBSites /></ProtectedRoute>} />
          <Route path="/wordpress/accounts" element={<ProtectedRoute><WPBAccounts /></ProtectedRoute>} />
          <Route path="/dcbot" element={<ProtectedRoute><DCBotRunner /></ProtectedRoute>} />
          <Route path="/forge/templates" element={<ProtectedRoute><ForgeTemplates /></ProtectedRoute>} />
          <Route path="/forge/deployments/new" element={<ProtectedRoute><ForgeNewDeployment /></ProtectedRoute>} />
          <Route path="/forge/urls" element={<ProtectedRoute><ForgeManageUrls /></ProtectedRoute>} />
          <Route path="/forge/wipe" element={<ProtectedRoute><ForgeMasterWipe /></ProtectedRoute>} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;